import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../../lib/useApi.js';
import { pageWindow, useUrlPagination } from '../../lib/usePagination.js';
import {
  PageHeader,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  StatusChip,
  PageViewerControls,
  KpiHeroBand,
  KpiBandSkeleton,
  StatusDonutMini,
  InlineScoreCell,
  Sparkline,
  MiniBars,
} from '../../components/index.js';
import { usePeople } from '../../lib/usePeople.js';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import './admin.css';
import './auditLog.redesign.css';

const SEVERITY_TONE = {
  critical: 'red',
  warn: 'amber',
  warning: 'amber',
  info: 'blue',
  success: 'sage',
};

// StatusDonutMini only knows the executive-traffic-light tone names; the legacy
// SEVERITY_TONE map uses 'red' for the strongest tone. Bridge it here so the
// severity-mix donut stays driven by the SAME source map.
const DONUT_TONE = { red: 'brick', amber: 'amber', blue: 'blue', sage: 'sage', slate: 'slate' };

// Canonical severity order for the mix donut (strongest first).
const SEVERITY_ORDER = ['critical', 'warning', 'info', 'success'];
const SEVERITY_LABEL = {
  critical: 'Critical',
  warning: 'Warning',
  info: 'Info',
  success: 'Success',
};

function severityOf(e) {
  const raw = e?.payload?.severity || (e?.result === 'success' ? 'info' : 'warn');
  const key = String(raw).toLowerCase();
  return key === 'warn' ? 'warning' : key;
}

const FILTER_LABELS = {
  action: 'Action',
  actor: 'Actor',
  targetType: 'Target type',
  targetId: 'Target ID',
  severity: 'Severity',
  fromDate: 'From',
  toDate: 'To',
};

// Plain-language names for the dotted audit action codes (AC-UX-2 "plain
// language first"). The raw code is still rendered as a muted mono secondary so
// operators keep the machine-readable string, but the primary label reads like
// a sentence — "Sam Baker · Approved access request · Granted role · for Parker
// Vaughn." Unknown codes fall back to a prettyStatus-style title-case split so
// no code ever renders as a raw dotted string in the primary slot.
const AUDIT_ACTION_LABELS = {
  'access_request.submit': 'Submitted access request',
  'access_request.approve': 'Approved access request',
  'access_request.deny': 'Denied access request',
  'access_request.requestRework': 'Returned for rework',
  'access_request.recertify': 'Recertified access',
  'access_request.revoke': 'Revoked access',
  'access_request.withdraw': 'Withdrew access request',
  'access.role.grant': 'Granted role',
  'access.provision.skipped': 'Provisioning skipped',
  'user.profile.update': 'Updated profile',
  'connector.sync': 'Synced connector',
  'custom_field.create': 'Created custom field',
  'custom_field.update': 'Updated custom field',
  'custom_field.delete': 'Deleted custom field',
  'feedback.create': 'Captured feedback',
  'feedback.convert': 'Converted feedback to action',
  'feedback.resolve': 'Resolved feedback',
};

// prettyStatus-style fallback (mirrors primitives.jsx:687-702) that also splits
// on the dot separator the dotted action codes use.
export function humanizeAuditAction(code) {
  if (code == null || code === '') return 'Activity';
  const known = AUDIT_ACTION_LABELS[code];
  if (known) return known;
  const spaced = String(code)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spaced) return String(code);
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function bucketTime(iso) {
  if (!iso) return 'Earlier';
  const t = new Date(iso).getTime();
  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const startYesterday = startToday - 24 * 3600 * 1000;
  if (t >= startToday) return 'Today';
  if (t >= startYesterday) return 'Yesterday';
  return 'Earlier';
}
function relTime(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}

function normalizeDateInput(value, boundary) {
  if (!value) return null;
  const suffix = boundary === 'end' ? 'T23:59:59.999Z' : 'T00:00:00.000Z';
  const date = new Date(`${value}${suffix}`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function AuditLogPage() {
  const { nameOf } = usePeople();
  const [filter, setFilter] = useState({
    action: '',
    actor: '',
    targetType: '',
    targetId: '',
    severity: '',
    fromDate: '',
    toDate: '',
  });
  const [params, setParams] = useSearchParams();
  const pager = useUrlPagination(params, setParams, { defaultPageSize: 10 });
  const audit = useApi('/audit-events', { query: { limit: 200 } });

  const events = useMemo(() => audit.data?.data || [], [audit.data]);
  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (filter.action && !`${e.action}`.toLowerCase().includes(filter.action.toLowerCase()))
        return false;
      if (filter.actor) {
        // Match the raw id OR the resolved display name (the name is what the feed renders).
        const needle = filter.actor.toLowerCase();
        const matchesActor =
          `${e.actorUserId}`.toLowerCase().includes(needle) ||
          nameOf(e.actorUserId).toLowerCase().includes(needle);
        if (!matchesActor) return false;
      }
      if (
        filter.targetType &&
        !`${e.targetType}`.toLowerCase().includes(filter.targetType.toLowerCase())
      )
        return false;
      if (filter.targetId && !`${e.targetId}`.toLowerCase().includes(filter.targetId.toLowerCase()))
        return false;
      if (
        filter.severity &&
        !`${e.payload?.severity || 'info'}`.toLowerCase().includes(filter.severity.toLowerCase())
      )
        return false;
      const occurredAt = e.occurredAt ? new Date(e.occurredAt) : null;
      if (filter.fromDate) {
        const from = normalizeDateInput(filter.fromDate, 'start');
        if (from && (!occurredAt || occurredAt < from)) return false;
      }
      if (filter.toDate) {
        const to = normalizeDateInput(filter.toDate, 'end');
        if (to && (!occurredAt || occurredAt > to)) return false;
      }
      return true;
    });
  }, [events, filter, nameOf]);
  const filteredWindow = pageWindow({
    offset: pager.offset,
    total: filtered.length,
    limit: pager.pageSize,
  });
  const visibleEvents = filtered.slice(
    filteredWindow.offset,
    filteredWindow.offset + filteredWindow.limit,
  );

  const grouped = useMemo(() => {
    const acc = { Today: [], Yesterday: [], Earlier: [] };
    for (const e of visibleEvents) acc[bucketTime(e.occurredAt)].push(e);
    return acc;
  }, [visibleEvents]);

  // Analytics derived from the already-fetched, filter-respecting result set.
  // The page previously discarded everything beyond the current page; these
  // rollups surface that data without any new fetch.
  const analytics = useMemo(() => {
    const severityCounts = { critical: 0, warning: 0, info: 0, success: 0 };
    const actorCounts = new Map();
    let elevated = 0; // critical + warning
    for (const e of filtered) {
      const sev = severityOf(e);
      if (severityCounts[sev] == null) severityCounts.info += 1;
      else severityCounts[sev] += 1;
      if (sev === 'critical' || sev === 'warning') elevated += 1;
      const actor = e.actorUserId || 'unknown';
      actorCounts.set(actor, (actorCounts.get(actor) || 0) + 1);
    }

    // Events-over-time: bucket by calendar day across the filtered window.
    const dayMs = 24 * 3600 * 1000;
    const dayCounts = new Map();
    let minDay = null;
    let maxDay = null;
    for (const e of filtered) {
      if (!e.occurredAt) continue;
      const d = new Date(e.occurredAt);
      if (Number.isNaN(d.getTime())) continue;
      const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      dayCounts.set(dayStart, (dayCounts.get(dayStart) || 0) + 1);
      if (minDay == null || dayStart < minDay) minDay = dayStart;
      if (maxDay == null || dayStart > maxDay) maxDay = dayStart;
    }
    let trend = [];
    if (minDay != null && maxDay != null) {
      const span = Math.min(60, Math.round((maxDay - minDay) / dayMs) + 1);
      for (let i = 0; i < span; i += 1) {
        trend.push(dayCounts.get(minDay + i * dayMs) || 0);
      }
    }
    const peak = trend.length ? Math.max(...trend) : 0;

    const severitySegments = SEVERITY_ORDER.map((key) => ({
      label: SEVERITY_LABEL[key],
      count: severityCounts[key],
      tone: DONUT_TONE[SEVERITY_TONE[key === 'warning' ? 'warning' : key]] || 'slate',
    }));

    const topActors = [...actorCounts.entries()]
      .map(([actor, count]) => ({ actor, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    const topActorMax = topActors.length ? topActors[0].count : 0;

    return {
      severityCounts,
      severitySegments,
      elevated,
      distinctActors: actorCounts.size,
      trend,
      peak,
      minDay,
      maxDay,
      topActors,
      topActorMax,
    };
  }, [filtered]);

  const kpiItems = useMemo(() => {
    const total = events.length;
    const shown = filtered.length;
    return [
      {
        id: 'volume',
        label: 'Events in scope',
        value: shown,
        unit: shown === 1 ? 'event' : 'events',
        footer: shown === total ? 'Full window (200 max)' : `Filtered from ${total}`,
        sparkline: analytics.trend.length > 1 ? analytics.trend : undefined,
        sparklineTone: 'blue',
        status: 'info',
      },
      {
        id: 'elevated',
        label: 'Elevated severity',
        value: analytics.elevated,
        unit: 'critical + warn',
        status: analytics.elevated > 0 ? 'warning' : 'success',
        statusLabel: analytics.elevated > 0 ? 'Attention' : 'Clear',
        footer: `${analytics.severityCounts.critical} critical · ${analytics.severityCounts.warning} warning`,
      },
      {
        id: 'actors',
        label: 'Distinct actors',
        value: analytics.distinctActors,
        unit: analytics.distinctActors === 1 ? 'actor' : 'actors',
        footer: analytics.topActors[0]
          ? `Most active: ${nameOf(analytics.topActors[0].actor)}`
          : 'No activity',
        status: 'info',
      },
    ];
  }, [events.length, filtered.length, analytics, nameOf]);

  const activeFilters = useMemo(
    () =>
      Object.entries(filter)
        .filter(([, v]) => v)
        .map(([key, value]) => ({ key, label: FILTER_LABELS[key] || key, value })),
    [filter],
  );

  const fmtDay = (ms) =>
    ms == null
      ? ''
      : new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  if (audit.loading)
    return (
      <>
        <PageHeader title="Audit log" />
        <KpiBandSkeleton count={3} />
        <LoadingSkeleton rows={6} />
      </>
    );
  if (audit.error) return <ErrorState error={audit.error} />;

  return (
    <>
      <PageHeader
        eyebrow="System control center · Audit"
        title="Audit log"
        subtitle="Every state-changing operation recorded for this workspace, timeline-grouped and filterable."
        actions={
          <div className="row">
            <input
              className="audit-filter"
              placeholder="action contains…"
              aria-label="Action"
              value={filter.action}
              onChange={(e) => setFilter((f) => ({ ...f, action: e.target.value }))}
            />
            <input
              className="audit-filter"
              placeholder="actor"
              aria-label="Actor"
              value={filter.actor}
              onChange={(e) => setFilter((f) => ({ ...f, actor: e.target.value }))}
            />
            <input
              className="audit-filter"
              aria-label="Target type"
              placeholder="target type"
              value={filter.targetType}
              onChange={(e) => setFilter((f) => ({ ...f, targetType: e.target.value }))}
            />
            <input
              className="audit-filter"
              aria-label="Target ID"
              placeholder="target ID"
              value={filter.targetId}
              onChange={(e) => setFilter((f) => ({ ...f, targetId: e.target.value }))}
            />
            <input
              className="audit-filter"
              placeholder="severity"
              aria-label="Severity"
              value={filter.severity}
              onChange={(e) => setFilter((f) => ({ ...f, severity: e.target.value }))}
            />
            <input
              className="audit-filter"
              aria-label="From date"
              type="date"
              value={filter.fromDate}
              onChange={(e) => setFilter((f) => ({ ...f, fromDate: e.target.value }))}
            />
            <input
              className="audit-filter"
              aria-label="To date"
              type="date"
              value={filter.toDate}
              onChange={(e) => setFilter((f) => ({ ...f, toDate: e.target.value }))}
            />
          </div>
        }
      />

      <KpiHeroBand
        eyebrow="Audit posture (this window)"
        asOf={audit.loadedAt}
        items={kpiItems}
        columns={3}
        className="audit-command-kpis"
      />

      {events.length > 0 ? (
        <section className="audit-command" aria-label="Audit analytics">
          <div className="card audit-command__trend">
            <div className="audit-command__head">
              <h2 className="card-title">Events over time</h2>
              <span className="audit-command__sub">
                {analytics.trend.length > 1 ? (
                  <>
                    peak <span className="audit-trend__peak">{analytics.peak}</span>/day
                  </>
                ) : (
                  'by calendar day'
                )}
              </span>
            </div>
            <div className="audit-trend__chart">
              {analytics.trend.length > 1 ? (
                <Sparkline
                  values={analytics.trend}
                  height={96}
                  tone="blue"
                  ariaLabel={`Audit events per day, ${analytics.trend.length} days`}
                />
              ) : analytics.trend.length === 1 ? (
                <MiniBars
                  values={analytics.trend}
                  height={96}
                  tone="blue"
                  ariaLabel="Audit events for the only day in scope"
                />
              ) : (
                <span className="audit-command__sub">No timestamped events in scope.</span>
              )}
            </div>
            {analytics.trend.length > 1 ? (
              <div className="audit-trend__axis">
                <span>{fmtDay(analytics.minDay)}</span>
                <span>{fmtDay(analytics.maxDay)}</span>
              </div>
            ) : null}
          </div>

          <div className="card">
            <div className="audit-command__head">
              <h2 className="card-title">Severity mix</h2>
              <span className="audit-command__sub">{filtered.length} in scope</span>
            </div>
            <div className="audit-donut-wrap">
              <StatusDonutMini
                segments={analytics.severitySegments}
                size={108}
                totalLabel="Events"
                ariaLabel="Severity distribution"
              />
            </div>
          </div>

          <div className="card">
            <div className="audit-command__head">
              <h2 className="card-title">Top actors</h2>
              <span className="audit-command__sub">{analytics.distinctActors} total</span>
            </div>
            {analytics.topActors.length ? (
              <ol className="audit-actors">
                {analytics.topActors.map((a, i) => (
                  <li key={a.actor} className="audit-actors__row">
                    <span className="audit-actors__rank">{i + 1}</span>
                    <span className="audit-actors__name" title={a.actor}>
                      {`${nameOf(a.actor)} · ${Math.round((a.count / (filtered.length || 1)) * 100)}%`}
                    </span>
                    <span className="audit-actors__score">
                      <InlineScoreCell
                        value={a.count}
                        max={analytics.topActorMax || 1}
                        tone="blue"
                        suffix={a.count === 1 ? ' event' : ' events'}
                        ariaLabel={`${nameOf(a.actor)}: ${a.count} events`}
                      />
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <span className="audit-command__sub">No actor activity in scope.</span>
            )}
          </div>
        </section>
      ) : null}

      {events.length > 0 ? (
        <div className="audit-filterbar" aria-label="Active filters and result count">
          {activeFilters.length ? (
            <>
              {activeFilters.map((f) => (
                <span key={f.key} className="audit-chip">
                  <span className="audit-chip__key">{f.label}:</span>
                  <span>{f.value}</span>
                  <button
                    type="button"
                    className="audit-chip__clear"
                    aria-label={`Clear ${f.label} filter`}
                    onClick={() => setFilter((prev) => ({ ...prev, [f.key]: '' }))}
                  >
                    ×
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="audit-filterbar__reset"
                onClick={() =>
                  setFilter({
                    action: '',
                    actor: '',
                    targetType: '',
                    targetId: '',
                    severity: '',
                    fromDate: '',
                    toDate: '',
                  })
                }
              >
                Clear all
              </button>
            </>
          ) : (
            <span className="audit-command__sub">No active filters</span>
          )}
          <span className="audit-filterbar__count">
            <strong>{filtered.length}</strong> of {events.length} events
          </span>
        </div>
      ) : null}

      <section className="bento admin-bento">
        <div className="span-12 card audit-feed">
          {events.length === 0 ? <EmptyState title="No audit events" /> : null}
          {['Today', 'Yesterday', 'Earlier'].map((bucket) =>
            grouped[bucket].length === 0 ? null : (
              <div key={bucket} className="audit-bucket">
                <div className="audit-bucket-head">
                  <span className="eyebrow">{bucket}</span>
                  <span className="muted small">{grouped[bucket].length} events</span>
                </div>
                <ul>
                  {grouped[bucket].map((e) => {
                    const sev = e.payload?.severity || (e.result === 'success' ? 'info' : 'warn');
                    const tone = SEVERITY_TONE[String(sev).toLowerCase()] || 'slate';
                    return (
                      <li key={e.id} className={`audit-row tone-${tone}`}>
                        <span className="audit-time mono">{relTime(e.occurredAt)}</span>
                        <span className="audit-actor">
                          <PersonLabel userId={e.actorUserId} fallback="system" />
                        </span>
                        <span className="audit-action">
                          <span className="audit-action__label">
                            {humanizeAuditAction(e.action)}
                          </span>
                          {e.action ? (
                            <span className="audit-action__code mono">{e.action}</span>
                          ) : null}
                        </span>
                        <span className="audit-target mono">{e.targetType}</span>
                        <span className="audit-target-id mono">{e.targetId}</span>
                        {e.requestId ? (
                          <span className="audit-request mono">{e.requestId}</span>
                        ) : null}
                        <StatusChip status={e.result || 'success'} />
                      </li>
                    );
                  })}
                </ul>
              </div>
            ),
          )}
          <PageViewerControls
            page={filteredWindow}
            label="audit events"
            pageSize={pager.pageSize}
            onOffsetChange={pager.setOffset}
            onPageSizeChange={pager.setPageSize}
          />
        </div>
      </section>
    </>
  );
}
